import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import { canManageUsers, canReadUsers, canWrite, hasPermission, type Permission } from '@/rbac/permissions'

export interface User {
  id: string
  email: string
  name: string
  role: 'ADMIN' | 'SUPPORT'
  auth_type?: 'entra' | 'local' | 'jwt'
}

export interface AuthConfig {
  entra_enabled: boolean
  local_auth_enabled: boolean
  security_enabled: boolean
}

// Storage helpers
function getUserFromStorage(): User | null {
  const stored = localStorage.getItem('user')
  if (stored) {
    try {
      return JSON.parse(stored) as User
    } catch {
      return null
    }
  }
  return null
}

function getAuthConfigFromStorage(): AuthConfig | null {
  const stored = localStorage.getItem('authConfig')
  if (stored) {
    try {
      return JSON.parse(stored) as AuthConfig
    } catch {
      return null
    }
  }
  return null
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'))
  const user = ref<User | null>(getUserFromStorage())
  const authConfig = ref<AuthConfig | null>(getAuthConfigFromStorage())
  const isCheckingAuth = ref(false)
  const authChecked = ref(false)

  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.role === 'ADMIN')
  const isSupport = computed(() => user.value?.role === 'SUPPORT')
  const canWriteApp = computed(() => canWrite(user.value?.role))
  const canManageUsersApp = computed(() => canManageUsers(user.value?.role))
  const canReadUsersApp = computed(() => canReadUsers(user.value?.role))
  const isEntraEnabled = computed(() => authConfig.value?.entra_enabled ?? false)
  const isLocalAuthEnabled = computed(() => authConfig.value?.local_auth_enabled ?? true)

  /**
   * Fetch auth configuration from the backend
   */
  async function fetchAuthConfig() {
    try {
      const response = await api.get('/auth/config')
      authConfig.value = response.data
      localStorage.setItem('authConfig', JSON.stringify(response.data))
      return response.data
    } catch (error) {
      console.error('Failed to fetch auth config:', error)
      // Default to local auth if we can't fetch config
      authConfig.value = {
        entra_enabled: false,
        local_auth_enabled: true,
        security_enabled: true
      }
      return authConfig.value
    }
  }

  /**
   * Check current authentication status with the backend
   * This checks both session (Entra) and JWT auth
   */
  async function checkAuth(): Promise<boolean> {
    if (isCheckingAuth.value) {
      return isAuthenticated.value
    }
    
    isCheckingAuth.value = true
    
    try {
      // Try to get current user from session endpoint
      const response = await fetch('/api/security/me', {
        credentials: 'include',
        headers: token.value ? { 'Authorization': `Bearer ${token.value}` } : {}
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.authenticated) {
          user.value = {
            id: data.id,
            email: data.email,
            name: data.name || data.email,
            role: data.role,
            auth_type: data.auth_type
          }
          localStorage.setItem('user', JSON.stringify(user.value))
          authChecked.value = true
          return true
        }
      }
      
      // Not authenticated
      clearAuthData()
      authChecked.value = true
      return false
    } catch (error) {
      console.error('Auth check failed:', error)
      // Don't clear on network error - might be temporary
      authChecked.value = true
      return isAuthenticated.value
    } finally {
      isCheckingAuth.value = false
    }
  }

  //  Login with email/password against users in the database (bcrypt).
  async function login(email: string, password: string) {
    const response = await api.post('/auth/login', { email, password })
    const data = response.data

    token.value = data.token
    refreshToken.value = data.refresh_token
    user.value = {
      ...data.user,
      auth_type: 'local'
    }

    localStorage.setItem('token', data.token)
    localStorage.setItem('refreshToken', data.refresh_token)
    localStorage.setItem('user', JSON.stringify(user.value))

    return data
  }

  /**
   * Login with legacy JWT endpoint (for backwards compatibility)
   */
  async function loginJWT(email: string, password: string) {
    const response = await api.post('/auth/login', { email, password })
    const data = response.data

    token.value = data.token
    refreshToken.value = data.refresh_token
    user.value = {
      ...data.user,
      auth_type: 'jwt'
    }

    localStorage.setItem('token', data.token)
    localStorage.setItem('refreshToken', data.refresh_token)
    localStorage.setItem('user', JSON.stringify(user.value))

    return data
  }

  /**
   * Redirect to Entra login
   */
  function loginWithEntra(returnUrl?: string) {
    let url = '/oauth2/authorization/entra'
    if (returnUrl) {
      url += `?return_url=${encodeURIComponent(returnUrl)}`
    }
    window.location.href = url
  }

  /**
   * Logout - clears both session and local storage
   */
  async function logout() {
    try {
      // Call backend logout to clear session
      await fetch('/logout', {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      console.error('Logout request failed:', error)
    }
    
    clearAuthData()
    
    // Redirect to login page
    window.location.href = '/login'
  }

  /**
   * Clear all auth data from local storage
   */
  function clearAuthData() {
    token.value = null
    refreshToken.value = null
    user.value = null

    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }

  /**
   * Refresh the JWT token
   */
  async function refresh() {
    if (!refreshToken.value) {
      throw new Error('No refresh token')
    }

    const response = await api.post('/auth/refresh', {
      refresh_token: refreshToken.value,
    })
    const data = response.data

    token.value = data.token
    localStorage.setItem('token', data.token)

    return data
  }

  /**
   * Check if user has one of the specified roles
   */
  function hasRole(roles: string[]) {
    return user.value && roles.includes(user.value.role)
  }

  /**
   * Check if the current user has a centralized RBAC permission
   */
  function checkPermission(permission: Permission) {
    return hasPermission(user.value?.role, permission)
  }

  /**
   * Initialize auth state
   * Called on app startup
   */
  async function init() {
    // Fetch auth config first
    await fetchAuthConfig()
    
    // Check authentication status
    await checkAuth()
  }

  return {
    token,
    refreshToken,
    user,
    authConfig,
    isCheckingAuth,
    authChecked,
    isAuthenticated,
    isAdmin,
    isSupport,
    canWrite: canWriteApp,
    canManageUsers: canManageUsersApp,
    canReadUsers: canReadUsersApp,
    isEntraEnabled,
    isLocalAuthEnabled,
    fetchAuthConfig,
    checkAuth,
    login,
    loginJWT,
    loginWithEntra,
    logout,
    clearAuthData,
    refresh,
    hasRole,
    hasPermission: checkPermission,
    init,
  }
})
